import Konva from "konva";
import { uid } from "uid";
import { deselectGroup, destroyTransformer, setId } from "../utils";
import { AppStage } from "@/core/AppStage";
import { mainGroups } from "@/core/MainGroups";

let builder;
let borderLayer = {};
let scene = null;
let transformStartBound;

//outline under the top line: arc by default, rectangle only when configs.rectangular === true
const getSceneOutlineAttrs = (width, height, rectangular, shiftX = 0, shiftY = 0) => {
  if (rectangular === true) {
    return {
      points: [shiftX, shiftY, shiftX, shiftY + height, shiftX + width, shiftY + height, shiftX + width, shiftY],
      tension: 0,
    };
  }

  return {
    points: [shiftX, shiftY, shiftX + width / 2, shiftY + height, shiftX + width, shiftY],
    tension: 0.75,
  };
};

//scene generation
export const generateScene = (configs, component, stageId, position) => {
  const appStage = new AppStage();
  builder = component;
  const id = stageId ? stageId : setId();
  const sceneWidth = configs && configs.width ? configs.width : 400;
  const sceneHeight = configs && configs.height ? configs.height : 120;
  const draggable = configs.draggable !== undefined ? configs.draggable : true;
  //create stage group component
  const sceneNameText = configs.text ? configs.text.toUpperCase() : "STAGE";
  if(!configs.text) {
    configs = {...configs, text: sceneNameText};
  }
  let mainGroup = new Konva.Group({
    id,
    configs,
    draggable: draggable,
    rotation: 0,
    width: sceneWidth,
    height: sceneHeight,
    type: 'stage',
    category: 'decor',
    transforming: true
  });

  //create layer for transforming
  let transformer = new Konva.Rect({
    width: sceneWidth,
    height: sceneHeight,
    type: "transform",
  })

  //create stage inner components - arc, top line, text and shape
  let arc = new Konva.Line({
    ...getSceneOutlineAttrs(sceneWidth, sceneHeight, configs.rectangular),
    stroke: '#fff',
    strokeWidth: 3,
    lineCap: 'round',
  });

  let top = new Konva.Line({
    points: [0, 0, sceneWidth, 0],
    stroke: '#fff',
    strokeWidth: 3,
  })

  let sceneName = new Konva.Text({
    text: sceneNameText,
    fill: "#fff",
    fontSize: 24,
    align: 'center',
    verticalAlign: 'middle',
    width: sceneWidth,
    height: sceneHeight,
    fontFamily: "Lato-Bold",
    lineJoin: 'bevel',
    letterSpacing: 5,
    type: "text",
  })

  let layerBorder = new Konva.Rect({
    width: sceneWidth + 20,
    height: sceneHeight + 20,
    x: -10,
    y: -10,
    strokeWidth: 1,
    dash: [5,5],
    type: "border",
  });

  //add stage components in group component
  mainGroup.add(top);
  mainGroup.add(arc);
  mainGroup.add(sceneName);
  if (configs.hideTitle === true) sceneName.visible(false);
  mainGroup.add(layerBorder);
  mainGroup.add(transformer);
  //set new values for variables
  mainGroups.add(mainGroup, id)
  // limitTrBoundBox(transformer);
  transformer.on("transformstart", () => {
    const konvaElement = mainGroups.get(id);
    const rotation = konvaElement.attrs.rotation;
    konvaElement.rotation(0);
    transformStartBound = transformer.getClientRect();
    konvaElement.rotation(rotation);
  })

  transformer.on("transform", () => {
    resizeScene(mainGroups.get(id), transformer, sceneName, top, arc, layerBorder, appStage.scaleX(), transformStartBound);
  })

  transformer.on("transformend", () => {
    const konvaElement = mainGroups.get(id);
    recountSize(konvaElement, transformer, sceneName, top, arc, layerBorder, appStage.scaleX(), appStage, configs)
  })

  borderLayer[id] = layerBorder;
  borderLayer[id].moveToTop();
  scene = mainGroup;

  if (position) {
    mainGroup.setAttrs({
      x: position.x,
      y: position.y,
      offsetX: sceneWidth / 2,
      offsetY: sceneHeight / 2,
    })

  } else {
    mainGroup.setAttrs({
      offsetX: sceneWidth / 2,
      offsetY: sceneHeight / 2,
      x: ((-appStage.x() + appStage.width() / 2) / appStage.scaleX()),
      y: (((-appStage.y() + 40) / appStage.scaleY() + (sceneHeight / 2))),
    });
  }

  sceneName.setAttrs({
    offsetX: sceneWidth / 2,
    offsetY: sceneHeight / 2,
    x: sceneWidth / 2,
    y: sceneHeight / 2,
  });

  const currentGroup = mainGroups.get(id);
  
  if(configs && configs.rotation) {
    rotateScene(id, configs.rotation, configs)
  }

  if(appStage.editing && currentGroup.attrs.draggable) {
    currentGroup.setAttrs({ 
      draggable: false,
      editDrag: true
    });
  }

  return { mainGroup, idScene: id, borderLayer };
};

//scene edit cancel
export const cancelSceneChanges = (configs) => {
  const appStage = new AppStage();
  const mainLayer = appStage.children[0];
  const { layerId } = configs;
  const layerElement = mainGroups.get(layerId);
  const position = layerElement.position();

  layerElement.destroy();

  const { mainGroup } = generateScene(
    configs,
    builder,
    layerId,
    // attrs,
    position
  );
  mainLayer.add(mainGroup);
  deselectGroup();
};

//rotate scene
export const rotateScene = (id, rotation, configs) => {
  // const appStage = new AppStage();
  const sceneElement = mainGroups.get(id);
  sceneElement.rotation(rotation);
  const normalizedConfigs = { ...sceneElement.attrs.configs, rotation };
  sceneElement.setAttrs({ configs: normalizedConfigs });

  const findText = sceneElement.children.find(
    (child) => child.attrs.type === "text"
  );

  if (rotation < -90 || rotation > 90) {
      findText.rotation(-180);
  } else {
      findText.rotation(0);
  }
};

export const changeSceneText = (id, text) => {
  const sceneElement = mainGroups.get(id);
  const findText = sceneElement.children.find(
    (child) => child.attrs.type === "text"
  );
  findText.setAttrs({
    text: text.toUpperCase()
  })
  const normalizedConfigs = { ...sceneElement.attrs.configs, text };
  sceneElement.setAttrs({ configs: normalizedConfigs });

}

//switch scene outline between arc and rectangle
export const changeSceneShape = (id, rectangular) => {
  const sceneElement = mainGroups.get(id);
  const normalizedConfigs = { ...sceneElement.attrs.configs };

  //the key is stored only when enabled, so arc scenes keep the same data as before
  if (rectangular) {
    normalizedConfigs.rectangular = true;
  } else {
    delete normalizedConfigs.rectangular;
  }

  sceneElement.setAttrs({ configs: normalizedConfigs });

  //the top line has 2 points, the outline (arc or rectangle) has more
  const arc = sceneElement.find("Line").find((line) => line.points().length > 4);
  arc.setAttrs(getSceneOutlineAttrs(sceneElement.width(), sceneElement.height(), normalizedConfigs.rectangular));
};

//show or hide scene title
export const changeSceneTitleVisibility = (id, hideTitle) => {
  const sceneElement = mainGroups.get(id);
  const normalizedConfigs = { ...sceneElement.attrs.configs };

  //the key is stored only when enabled, so existing scenes keep the same data as before
  if (hideTitle) {
    normalizedConfigs.hideTitle = true;
  } else {
    delete normalizedConfigs.hideTitle;
  }

  sceneElement.setAttrs({ configs: normalizedConfigs });

  const findText = sceneElement.children.find(
    (child) => child.attrs.type === "text"
  );
  findText.visible(!normalizedConfigs.hideTitle);
};

//update scene
export const updateSceneStage = (id, payload) => {
  const sceneElement = mainGroups.get(id);
  const rotation = sceneElement.rotation();
  if (rotation){
    sceneElement.rotation(rotation);
  }
};

export const stageActions = {
  editAction: (id, component) => {
    const currentGroup = mainGroups.get(id);
    const appStage = new AppStage();
    AppStage.addKey('editing', id);

    const confs = currentGroup.attrs.configs;
    component.editScene(id, confs, currentGroup);

    if(appStage.transformer) {
      destroyTransformer();
    }

    borderLayer[id].setAttrs({stroke: ''});
  },
  copyAction: (id, component, configs) => {
    const currentGroup = mainGroups.get(id);
    const appStage = new AppStage();
    const mainLayer = appStage.children[0];

    const conf = currentGroup.attrs.configs;
    const updatedConf = {...conf, draggable: true};
    const { mainGroup } = generateScene(updatedConf, component);
    mainLayer.add(mainGroup);
    deselectGroup();
    borderLayer[id].setAttrs({stroke: ''});
  }
}

export const copyStageElementAtPosition = (id, component, position) => {
  const currentGroup = mainGroups.get(id);
  const appStage = new AppStage();
  const mainLayer = appStage.children[0];
  const conf = currentGroup.attrs.configs;
  const updatedConf = { ...conf, draggable: true };
  const { mainGroup } = generateScene(updatedConf, component, undefined, position);
  mainLayer.add(mainGroup);
  return mainGroup.attrs.id;
};

//resize and reposition scene elements during transform
const resizeScene = (group, transformer, name, top, arc, border, scale, start) => {
  const rotation = group.attrs.rotation;

  if(rotation !== 0) {
    group.rotation(0);
    // name.rotation(0);
  }

  const newParams = transformer.getClientRect();
  const newWidth = newParams.width / scale;
  const newHeight = newParams.height / scale;
  const shiftX = (newParams.x - start.x) /scale;
  const shiftY = (newParams.y - start.y) /scale;
  let nameOffsetX = 0;
  let nameOffsetY = 0;

  if (rotation < -90 || rotation > 90) {
    nameOffsetX = newWidth;
    nameOffsetY = newHeight;
  }

  name.setAttrs({
    width: newWidth,
    height: newHeight,
    x: shiftX,
    y: shiftY,
    offsetX: nameOffsetX,
    offsetY: nameOffsetY,
  });

  top.setAttrs({
    width: newWidth,
    height: newHeight,
    points: [
      shiftX,
      shiftY,
      shiftX + newWidth,
      shiftY,
    ],
  });

  arc.setAttrs({
    width: newWidth,
    height: newHeight,
    ...getSceneOutlineAttrs(newWidth, newHeight, group.attrs.configs.rectangular, shiftX, shiftY),
  })

  border.setAttrs({
    width: newWidth + 20,
    height: newHeight + 20,
    offsetX: -shiftX,
    offsetY: -shiftY,
  })

  if(rotation) {
    group.rotation(rotation);
    // if (rotation < -90 || rotation > 90) {
    //     name.rotation(180);
    // } else {
    //     name.rotation(0);
    // }
  }
}

//resize and reposition scene and scene elements after transform
const recountSize = (group, transformer, name, top, arc, border, scale, stage, configs) => {
  const appStage = new AppStage();
  let newParams = transformer.getClientRect();

  const currentWidth = newParams.width/scale;
  const currentHeight = newParams.height/scale;
  const currentX = newParams.x/scale;
  const currentY = newParams.y/scale;
  let finalWidth = currentWidth;
  let finalHeight = currentHeight;
  const rotation = group.attrs.rotation;

  if(rotation !== 0) {
    group.rotation(0);
    newParams = transformer.getClientRect();
    finalWidth = newParams.width/scale;
    finalHeight =  newParams.height/scale;
  }

  const resetedPosition = {
    x: 0,
    y: 0
  }

  group.setAttrs({
    width: finalWidth,
    height: finalHeight,
    offsetX: finalWidth / 2,
    offsetY: finalHeight / 2,
  })

  transformer.setAttrs({
    width: finalWidth,
    height: finalHeight,
    scaleX: 1,
    scaleY: 1,
  })

  name.setAttrs({
    width: finalWidth,
    height: finalHeight,
    offsetX: finalWidth / 2,
    offsetY: finalHeight / 2,
    x: finalWidth / 2,
    y: finalHeight / 2,
  });

  top.setAttrs({
    points: [0, 0, finalWidth , 0],
  });

  arc.setAttrs(getSceneOutlineAttrs(finalWidth, finalHeight, group.attrs.configs.rectangular))

  border.setAttrs({
    width: finalWidth + 20,
    height: finalHeight + 20,
    offsetX: 0,
    offsetY: 0,
  })

  group.position({
    x: currentX + currentWidth / 2 - appStage.x() / scale,
    y: currentY + currentHeight / 2 - appStage.y() / scale,
  });

  transformer.position(resetedPosition);

  if(rotation !== 0) {
    group.rotation(rotation);
  }

  const normalizedConfigs =  { ...group.attrs.configs, width: finalWidth, height: finalHeight };
  group.setAttrs({ configs: normalizedConfigs });

}
